import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Lightbulb, CornerDownRight, ChefHat, Send } from "lucide-react";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

function Avatar({ user, isChef }) {
  return (
    <div className="relative shrink-0">
      {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover border border-border" />
        : <span className="grid place-items-center w-9 h-9 rounded-full bg-secondary text-ink font-bold text-sm">{(user?.name || "M")[0].toUpperCase()}</span>}
      {isChef && <span className="absolute -bottom-1 -right-1 grid place-items-center w-4.5 h-4.5 w-[18px] h-[18px] rounded-full bg-coral text-white"><ChefHat size={10} /></span>}
    </div>
  );
}

function Composer({ onSubmit, placeholder, type, compact, testId }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = async () => {
    if (!user) { toast("Log in to join the conversation"); navigate("/login"); return; }
    if (!text.trim()) return;
    setBusy(true);
    try { await onSubmit(text.trim()); setText(""); } finally { setBusy(false); }
  };
  return (
    <div className={`flex gap-2 ${compact ? "mt-2" : "mt-4"}`}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={compact ? 1 : 2} placeholder={placeholder} data-testid={`${testId}-input`}
        onFocus={() => { if (!user) { toast("Log in to join the conversation"); navigate("/login"); } }}
        className="flex-1 min-w-0 rounded-xl border border-border bg-white px-4 py-2.5 text-base outline-none focus:border-coral focus:ring-4 focus:ring-coral/10 font-medium resize-none" />
      <button onClick={go} disabled={busy || !text.trim()} data-testid={`${testId}-submit`} aria-label="Post"
        className={`shrink-0 inline-flex items-center gap-2 rounded-full font-bold text-white transition-colors disabled:opacity-50 ${compact ? "h-11 w-11 justify-center" : "h-12 px-5"} ${type === "suggestion" ? "bg-ink hover:bg-ink-soft" : "bg-coral hover:bg-coral-hover"}`}>
        <Send size={16} />{!compact && (type === "suggestion" ? "Suggest" : "Comment")}
      </button>
    </div>
  );
}

function Item({ item, onReply, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex gap-3" data-testid={`${testId}-item-${item.id}`}>
      <Avatar user={item.user} isChef={item.is_chef} />
      <div className="flex-1 min-w-0">
        <div className="rounded-2xl bg-white border border-border px-4 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-ink text-sm">{item.user?.name}</span>
            {item.is_chef && <span className="rounded-full bg-coral/10 text-coral text-[10px] font-black uppercase tracking-wider px-2 py-0.5">Chef</span>}
            {item.type === "suggestion" && !item.parent_id && <span className="rounded-full bg-gold/15 text-amber-700 text-[10px] font-black uppercase tracking-wider px-2 py-0.5">Suggestion</span>}
            <span className="text-xs text-ink-soft ml-auto">{timeAgo(item.created_at)}</span>
          </div>
          <p className="mt-1.5 text-ink leading-relaxed whitespace-pre-line break-words">{item.content}</p>
        </div>
        <div className="mt-1.5 flex items-center gap-3 px-1">
          <button onClick={() => setOpen((o) => !o)} data-testid={`${testId}-reply-toggle-${item.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-ink-soft hover:text-coral min-h-[32px]"><CornerDownRight size={13} /> Reply{item.replies?.length ? ` (${item.replies.length})` : ""}</button>
        </div>
        {item.replies?.length > 0 && (
          <div className="mt-2 space-y-2 pl-2 border-l-2 border-border ml-1">
            {item.replies.map((r) => (
              <div key={r.id} className="flex gap-2.5 pl-2" data-testid={`${testId}-reply-${r.id}`}>
                <Avatar user={r.user} isChef={r.is_chef} />
                <div className="flex-1 min-w-0 rounded-2xl bg-secondary/60 px-3.5 py-2.5">
                  <div className="flex items-center gap-2"><span className="font-bold text-ink text-sm">{r.user?.name}</span>{r.is_chef && <span className="rounded-full bg-coral/10 text-coral text-[10px] font-black uppercase tracking-wider px-2 py-0.5">Chef</span>}<span className="text-xs text-ink-soft ml-auto">{timeAgo(r.created_at)}</span></div>
                  <p className="mt-1 text-ink text-sm leading-relaxed whitespace-pre-line break-words">{r.content}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {open && <Composer compact type="comment" placeholder="Write a reply…" testId={`${testId}-reply-${item.id}`} onSubmit={async (t) => { await onReply(item.id, t); setOpen(false); }} />}
      </div>
    </div>
  );
}

export function CommentThread({ items, setItems, postUrl, testId = "thread" }) {
  const [tab, setTab] = useState("comment");
  const list = (items || []).filter((i) => i.type === tab);

  const post = async (content, parentId) => {
    try {
      const { data } = await api.post(postUrl, { type: tab, content, parent_id: parentId || null });
      setItems((prev) => {
        if (!parentId) return [data, ...prev];
        return prev.map((i) => (i.id === parentId ? { ...i, replies: [...(i.replies || []), data] } : i));
      });
      toast.success(parentId ? "Reply posted" : tab === "suggestion" ? "Suggestion sent to the chef" : "Comment posted");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      throw err;
    }
  };

  const TABS = [["comment", MessageCircle, "Comments"], ["suggestion", Lightbulb, "Suggestions"]];
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2">
        {TABS.map(([k, Icon, label]) => {
          const n = (items || []).filter((i) => i.type === k).length;
          return (
            <button key={k} onClick={() => setTab(k)} data-testid={`${testId}-tab-${k}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 h-11 text-sm font-bold transition-colors ${tab === k ? "bg-ink text-white" : "bg-white border border-border text-ink-soft hover:border-coral"}`}>
              <Icon size={15} /> {label} <span className={`text-xs rounded-full px-1.5 ${tab === k ? "bg-white/20" : "bg-secondary"}`}>{n}</span>
            </button>
          );
        })}
      </div>
      <Composer type={tab} testId={`${testId}-${tab}`}
        placeholder={tab === "suggestion" ? "Try adding… (share an idea for the chef)" : "Share your thoughts…"}
        onSubmit={(t) => post(t)} />
      <div className="mt-6 space-y-5">
        {list.length === 0 ? (
          <p className="text-sm text-ink-soft text-center py-6">{tab === "suggestion" ? "No suggestions yet — be the first to share an idea." : "No comments yet. Start the conversation!"}</p>
        ) : list.map((i) => <Item key={i.id} item={i} testId={testId} onReply={(pid, t) => post(t, pid)} />)}
      </div>
    </div>
  );
}
